import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import api, { API_ENDPOINTS } from '../config/api';
import JobHideToggleButton from '../components/JobHideToggleButton';
import toast from 'react-hot-toast';
import { 
  ArrowLeft, 
  MapPin, 
  Clock, 
  DollarSign, 
  Users,
  Star,
  Calendar,
  MessageCircle,
  Edit,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
  Briefcase
} from 'lucide-react';

const JobDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { socket } = useSocket();
  const queryClient = useQueryClient();
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [applicationData, setApplicationData] = useState({
    coverLetter: '',
    bidAmount: '',
    estimatedDuration: ''
  });

  // Fetch job details
  const { data: job, isLoading, error, refetch } = useQuery(
    ['job', id],
    () => api.get(API_ENDPOINTS.JOBS.DETAIL(id)),
    {
      select: (data) => data.data,
      enabled: !!id,
    }
  );

  // Refetch job data when user changes (e.g., role switch)
  useEffect(() => {
    if (user?._id && id) {
      refetch();
    }
  }, [user?._id, user?.role, id, refetch]);

  // Listen for job updates via socket
  useEffect(() => {
    if (!socket || !id) return;

    socket.on('job:updated', (updatedJob) => {
      if (updatedJob._id === id) {
        queryClient.setQueryData(['job', id], { data: updatedJob });
      }
    });

    return () => {
      socket.off('job:updated');
    };
  }, [socket, id, queryClient]);

  // Check if user has already applied (but allow multiple applications)
  const userApplications = job?.applications?.filter(app => app.talent?.toString() === user?._id || app.talent?.toString() === user?.id) || [];
  const latestUserApplication = userApplications.length > 0 ? userApplications[userApplications.length - 1] : null;
  // Check if user is the job owner - must check both _id and id, and ensure user role is client
  const isJobOwner = user?.role === 'client' && (job?.client?._id?.toString() === user?._id?.toString() || job?.client?._id?.toString() === user?.id?.toString() || job?.client?.toString() === user?._id?.toString() || job?.client?.toString() === user?.id?.toString());

  // Apply to job mutation
  const applyMutation = useMutation(
    (data) => api.post(API_ENDPOINTS.JOBS.APPLY(id), data),
    {
      onSuccess: () => {
        toast.success('Application submitted successfully!');
        queryClient.invalidateQueries(['job', id]);
        setShowApplicationForm(false);
        setApplicationData({ coverLetter: '', bidAmount: '', estimatedDuration: '' });
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to submit application');
      }
    }
  );

  // Approve application mutation
  const approveApplicationMutation = useMutation(
    (applicationId) => api.post(API_ENDPOINTS.JOBS.APPROVE_APPLICATION(id, applicationId)),
    {
      onSuccess: (response) => {
        toast.success('Application approved successfully!');
        queryClient.invalidateQueries(['job', id]);
        // Redirect to chat if chat ID is returned
        if (response.data?.chat) {
          navigate(`/chats/${response.data.chat}`);
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to approve application');
      }
    }
  );

  // Reject application mutation
  const rejectApplicationMutation = useMutation(
    (applicationId) => api.post(API_ENDPOINTS.JOBS.REJECT_APPLICATION(id, applicationId)),
    {
      onSuccess: () => {
        toast.success('Application rejected');
        queryClient.invalidateQueries(['job', id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to reject application');
      }
    }
  );

  const handleApply = (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Please login to apply for this job');
      return;
    }
    applyMutation.mutate(applicationData);
  };

  const handleApproveApplication = (applicationId) => {
    approveApplicationMutation.mutate(applicationId);
  };

  const handleRejectApplication = (applicationId) => {
    rejectApplicationMutation.mutate(applicationId);
  };

  const formatBudget = (job) => {
    if (!job.budget) return 'Budget negotiable';
    if (job.budget.fixed) return `$${job.budget.fixed.toFixed(2)}`;
    if (job.budget.min && job.budget.max) {
      return `$${job.budget.min.toFixed(2)} - $${job.budget.max.toFixed(2)}`;
    }
    if (job.budget.min) return `$${job.budget.min.toFixed(2)}+`;
    return 'Budget negotiable';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      accepted: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      rejected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'in-progress': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-6xl mb-4">😞</div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Job not found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            The job you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/jobs" className="btn-primary">
            Browse Jobs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-accent mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>

        {/* Job Image */}
        {job.imageUrl && (
          <div className="mb-6 rounded-lg overflow-hidden">
            <img 
              src={job.imageUrl} 
              alt={job.title}
              className="w-full h-64 md:h-96 object-cover"
            />
          </div>
        )}
        
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-heading mb-2">
              {job.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-sm text-gray-600 dark:text-gray-400 mb-4">
              <div className="flex items-center space-x-1">
                <DollarSign className="w-4 h-4" />
                <span>{formatBudget(job)}</span>
              </div>
              
              {job.duration && job.duration.value && job.duration.unit && (
                <div className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>{job.duration.value} {job.duration.unit}</span>
                </div>
              )}

              {job.location && (
                <div className="flex items-center space-x-1">
                  <MapPin className="w-4 h-4" />
                  <span className="whitespace-nowrap">{job.location.remote ? 'Remote' : job.location.city || 'Location not specified'}</span>
                </div>
              )}

              <div className="flex items-center space-x-1">
                <Users className="w-4 h-4" />
                <span>{job.applicationCount} applications</span>
              </div>

              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span className="whitespace-nowrap">Posted {new Date(job.createdAt).toLocaleDateString()}</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                job.type === 'full-time' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                job.type === 'part-time' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                job.type === 'freelance' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
              }`}>
                {(job.type
                  ? job.type.charAt(0).toUpperCase() + job.type.slice(1)
                  : 'Job')}
              </span>
              {/* Show completed count after type tag - only if there are completed applications */}
              {job.applicationStatusCounts?.completed > 0 && (
                <span className="px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                  Completed ({job.applicationStatusCounts.completed})
                </span>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 lg:flex-shrink-0">
            {isJobOwner ? (
              <>
                <Link
                  to={`/jobs/${job._id}/applications`}
                  className="btn-outline"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Applications ({job.applicationCount})
                </Link>
                <JobHideToggleButton
                  jobId={job._id}
                  jobTitle={job.title}
                  isHidden={!!job.isHiddenFromListing}
                  invalidateKeys={['job', id]}
                  className="btn-outline !p-2"
                />
              </>
            ) : (
              <>
                {!isAuthenticated ? (
                  <Link to="/login" className="btn-primary">
                    Login to Apply
                  </Link>
                ) : user?.role === 'talent' ? (
                  <>
                    {job.isHiddenFromListing && !latestUserApplication ? (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        This job is not accepting new applications.
                      </p>
                    ) : (
                      <>
                    {latestUserApplication && (
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mb-2">
                        <span className={`px-3 py-1 text-xs font-medium rounded-full text-center sm:text-left ${getStatusColor(latestUserApplication?.status)}`}>
                          Latest: {latestUserApplication?.status?.charAt(0).toUpperCase() + latestUserApplication?.status?.slice(1)}
                        </span>
                        {(latestUserApplication?.status === 'accepted' || latestUserApplication?.status === 'in-progress') && latestUserApplication?.chatId && (
                          <Link to={`/chats/${latestUserApplication.chatId}`} className="btn-primary text-center text-sm">
                            <MessageCircle className="w-4 h-4 mr-2" />
                            Open Chat
                          </Link>
                        )}
                      </div>
                    )}
                    <button
                      onClick={() => {
                        if (latestUserApplication) {
                          setShowWarningModal(true);
                        } else {
                          setShowApplicationForm(true);
                        }
                      }}
                      className="btn-primary"
                    >
                      {latestUserApplication ? 'Apply Again' : 'Apply Now'}
                    </button>
                      </>
                    )}
                  </>
                ) : (
                  <Link to="/login" className="btn-primary">
                    Login to Apply
                  </Link>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Job Description */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Job Description
            </h2>
            <div 
              className="prose prose-gray dark:prose-invert max-w-none text-gray-700 dark:text-gray-300"
              dangerouslySetInnerHTML={{ __html: job.description }}
            />
          </div>

          {/* Skills Required */}
          {job.skills && job.skills.length > 0 && (
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Required Skills
              </h2>
              <div className="flex flex-wrap gap-2">
                {job.skills.map((skill, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm rounded-md"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Requirements */}
          {job.requirements && job.requirements.filter(req => req && req.trim() !== '' && req !== '[""]').length > 0 && (
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Requirements
              </h2>
              <ul className="space-y-2">
                {job.requirements.filter(req => req && req.trim() !== '' && req !== '[""]').map((requirement, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">{requirement}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Deliverables */}
          {job.deliverables && job.deliverables.filter(del => del && del.trim() !== '' && del !== '[""]').length > 0 && (
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Deliverables
              </h2>
              <ul className="space-y-2">
                {job.deliverables.filter(del => del && del.trim() !== '' && del !== '[""]').map((deliverable, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">{deliverable}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Client Info */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Client Information
            </h3>
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center text-white text-lg font-medium">
                {job.client?.username?.[0].toUpperCase()}
              </div>
              <div>
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  {job.client?.username}
                </div>
                <div className="flex items-center space-x-1 text-sm text-gray-500">
                  <Star className="w-4 h-4 fill-current text-yellow-400" />
                  <span>{job.client?.stats?.rating?.average?.toFixed(1) || '0'}</span>
                  <span>({job.client?.stats?.rating?.count || 0} reviews)</span>
                </div>
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-center justify-between mb-1">
                <span>Jobs Posted:</span>
                <span className="font-medium">{job.client?.stats?.jobsPosted || 0}</span>
              </div>
              <div className="flex items-center justify-between mb-1">
                <span>Success Rate:</span>
                <span className="font-medium">{job.client?.stats?.successRate || 0}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Member Since:</span>
                <span className="font-medium">{new Date(job.client?.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {/* Job Stats */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Job Statistics
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Applications</span>
                <span className="font-medium">{job.applicationCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Views</span>
                <span className="font-medium">{job.views || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Posted</span>
                <span className="font-medium">{new Date(job.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Warning Modal for Multiple Applications */}
      {showWarningModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Already Applied
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              You've already submitted an application for this job. Submitting another application may not boost your chances.
            </p>
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => setShowWarningModal(false)}
                className="btn-outline flex-1"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowWarningModal(false);
                  setShowApplicationForm(true);
                }}
                className="btn-primary flex-1"
              >
                Proceed to Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Application Form Modal */}
      {showApplicationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Apply for this Job
            </h3>
            <form onSubmit={handleApply} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cover Letter *
                </label>
                <textarea
                  value={applicationData.coverLetter}
                  onChange={(e) => setApplicationData(prev => ({ ...prev, coverLetter: e.target.value }))}
                  rows={4}
                  className="w-full input-field"
                  placeholder="Tell the client why you're the best fit for this job..."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Your Bid Amount ($) *
                </label>
                <input
                  type="number"
                  min="1"
                  value={applicationData.bidAmount}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow positive integers
                    if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 1)) {
                      setApplicationData(prev => ({ ...prev, bidAmount: value }));
                    }
                  }}
                  className="w-full input-field"
                  placeholder="Enter your bid amount"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Estimated Duration (days) *
                </label>
                <input
                  type="number"
                  min="1"
                  value={applicationData.estimatedDuration}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow positive integers
                    if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 1)) {
                      setApplicationData(prev => ({ ...prev, estimatedDuration: value }));
                    }
                  }}
                  className="w-full input-field"
                  placeholder="How many days will it take?"
                  required
                />
              </div>
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() => setShowApplicationForm(false)}
                  className="btn-outline flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={applyMutation.isLoading}
                  className="btn-primary flex-1"
                >
                  {applyMutation.isLoading ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetailPage;